import { debounce } from "~/lib/utils";
import { useI18n } from "vue-i18n";
import { DateFormatter } from "@internationalized/date";
import { useFetchAPI } from "./useFetchAPI";

const df = new DateFormatter();

// Symbol for unique context identification
const PostsSymbol = Symbol( "posts" );

// Create the posts context (call this in parent component)
export function createPostsContext () {
    const route = useRoute();
    const { t } = useI18n();
    const {
        fetchAllPosts: fetchAllPostsAPI,
        fetchSearchPosts: fetchSearchPostsAPI,
        fetchPostCategory: fetchPostCategoryAPI,
    } = useFetchAPI();

    const maxPostsPerPage = 10;
    // Constants for URL parameters
    const searchParamName = "q";
    const filterParamName = "filter";

    const sortParamName = "sort";
    const pageParamName = "page";
    const defaultFilter = t( "toolbar.filter.options.newsPage.all" );
    const defaultSort = t( "toolbar.sort.options.newsPage.newest" );

    // Create the shared state
    const state = reactive( {
        category: [],
        highlightedPosts: [],
        posts: [],
        searchPosts: [],
        shownPosts: [],
        isPending: false,
        error: null,
        search: route.query[ searchParamName ]?.toString() || "",
        selectedFilter: route.query[ filterParamName ]?.toString() || null,
        selectedSort: route.query[ sortParamName ]?.toString() || null,
        selectedDate: { start: null, end: null },
        currentPage: parseInt(
            route.query[ pageParamName ]?.toString() || "1",
            10,
        ),
        totalPages: 1,
    } );

    const filterOptions = {
        wheels: [
            {
                data: [ t( "toolbar.filter.options.newsPage.all" ) ],
            },
        ],
        initValue: defaultFilter,
    };

    const sortOptions = {
        wheels: [
            {
                data: [
                    t( "toolbar.sort.options.newsPage.newest" ),
                    t( "toolbar.sort.options.newsPage.oldest" ),
                ],
            },
        ],
        initValue: defaultSort,
    };

    function getPostsByPage ( source, page ) {
        const start = ( page - 1 ) * maxPostsPerPage;
        const end = start + maxPostsPerPage;
        return source.slice( start, end );
    }
    function getTotalPages ( source ) {
        const totalPages = Math.ceil( source.length / maxPostsPerPage );
        return totalPages === 0 ? 1 : totalPages;
    }
    function mockLoading () {
        debounce( () => {
            state.isPending = false;
        }, 1000 )();
    }
    // URL Synchronization - same watch logic as current implementation
    // watch(
    //     () => [
    //         state.search,
    //         state.selectedFilter,
    //         state.selectedSort,
    //         state.currentPage,
    //     ],
    //     (
    //         [newSearch, newFilter, newSort, newPage],
    //         [oldSearch, oldFilter, oldSort, oldPage],
    //     ) => {
    //         // Basic check to prevent loop if router.replace triggers watch immediately
    //         if (
    //             newSearch === oldSearch &&
    //             newFilter === oldFilter &&
    //             newSort === oldSort &&
    //             newPage === oldPage
    //         ) {
    //             return;
    //         }

    //         const query = {};
    //         if (newSearch) query[searchParamName] = newSearch;
    //         if (newFilter && newFilter !== defaultFilter)
    //             query[filterParamName] = newFilter;
    //         if (newSort && newSort !== defaultSort)
    //             query[sortParamName] = newSort;
    //         if (newPage && newPage !== 1) query[pageParamName] = newPage;

    //         const searchParams = new URLSearchParams(query).toString();
    //         const newUrl = window.location.pathname + "?" + searchParams;
    //         window.history.replaceState(null, "", newUrl);
    //     },
    //     { deep: true },
    // );

    const fetchPosts = async ( type = "news", highlightedIDs = [] ) => {
        if ( state.isPending ) return;

        const { data: categoryData, error: categoryError } =
            await fetchPostCategoryAPI( type === "news" ? "news" : "award" );
        if ( categoryError?.value ) {
            // console.error("Error fetching post category:", categoryError.value);
        } else {
            state.category = categoryData.value;
            filterOptions.wheels[ 0 ].data = [
                ...filterOptions.wheels[ 0 ].data,
                ...state.category.map( ( item ) => item.name ),
            ];
        }

        state.isPending = true;
        state.error = null;
        try {
            let data, error;

            if ( type === "news" ) {
                // Fetch both news and awards
                const newsResponse = await fetchAllPostsAPI( "news" );
                data = newsResponse.data;
                error = newsResponse.error;
            } else {
                // Fetch only awards
                const awardsResponse = await fetchAllPostsAPI( "award" );
                data = awardsResponse.data;
                error = awardsResponse.error;
            }

            if ( error?.value ) {
                throw new Error( error );
            }

            // modify the data to add the category name
            if ( data.value && data.value.length > 0 ) {
                const map = new Map(
                    state.category.map( ( item ) => [ item.id, item.name ] ),
                );

                data.value.forEach( ( item ) => {
                    if ( type === "news" ) {
                        item[ "news-category" ] = item[ "news-category" ].map(
                            ( id ) => map.get( id ) ?? `Unknown(${ id })`,
                        );
                    } else {
                        item[ "award-category" ] = item[ "award-category" ].map(
                            ( id ) => map.get( id ) ?? `Unknown(${ id })`,
                        );
                    }
                } );
            }

            if ( type === "news" ) {
                // Type safety checks
                const safeData = Array.isArray( data.value ) ? data.value : [];
                const safeHighlightedIDs = Array.isArray( highlightedIDs )
                    ? highlightedIDs
                    : [];

                // Highlighted posts are the with IDs in highlightedIDs
                // 保持 highlightedPosts 的順序與 safeHighlightedIDs 一致
                state.highlightedPosts = safeHighlightedIDs
                    .map( ( id ) => safeData.find(
                        ( post ) => post && post.id !== undefined && post.id === id,
                    ) )
                    .filter( Boolean );
                // Posts should exclude the highlighted posts
                state.posts = safeData.filter(
                    ( post ) =>
                        post &&
                        post.id !== undefined &&
                        !safeHighlightedIDs.includes( post.id ),
                );
            } else {
                state.posts = Array.isArray( data.value ) ? data.value : [];
            }
            state.totalPages = getTotalPages( state.posts );
            state.shownPosts = getPostsByPage( state.posts, state.currentPage );
        } catch ( err ) {
            // console.error(`Error fetching ${type}:`, err);
            state.error = err.message || "An error occurred";
            return { data: null, error: state.error };
        } finally {
            state.isPending = false;
        }
    };

    // Reuse existing handler functions, but update state directly
    function handlePageChange ( newPage ) {
        if ( newPage !== state.currentPage ) {
            state.currentPage = newPage;
            if ( state.search ) {
                state.shownPosts = getPostsByPage(
                    state.searchPosts,
                    state.currentPage,
                );
            } else {
                state.shownPosts = getPostsByPage(
                    state.posts,
                    state.currentPage,
                );
            }
            window.scrollTo( {
                top: 0,
                behavior: "smooth",
            } );
        }
    }

    async function handleSearchChange ( value, type ) {
        state.search = value;
        state.currentPage = 1; // Reset pagination when search changes
        handlePageChange( state.currentPage );
        state.isPending = true;
        const { data, error } = await fetchSearchPostsAPI( value, type );
        state.searchPosts = data;
        state.totalPages = getTotalPages( state.searchPosts );
        state.shownPosts = getPostsByPage( state.searchPosts, state.currentPage );
        state.error = error;
        mockLoading();
    }
    function handleCancelSearch () {
        state.isPending = true;
        state.search = "";
        state.currentPage = 1; // Reset pagination when search changes
        state.totalPages = getTotalPages( state.posts );
        state.shownPosts = getPostsByPage( state.posts, state.currentPage );
        mockLoading();
    }
    function handleFilterChange ( value ) {
        state.isPending = true;
        state.selectedFilter = value;
        state.currentPage = 1; // Reset pagination when filter changes

        if ( value === t( "toolbar.filter.options.newsPage.all" ) ) {
            state.shownPosts = state.posts;
        } else if (
            value &&
            value !== t( "toolbar.filter.options.newsPage.all" )
        ) {
            state.shownPosts = state.posts.filter( ( post ) =>
                post.type === "news"
                    ? // TEMP: 一篇新聞只能顯示一個分類設計
                    post[ "news-category" ].includes( value )
                    : post[ "award-category" ].includes( value ),
            );
        } else {
            state.shownPosts = state.posts;
        }

        mockLoading();
    }

    function handleSortChange ( value ) {
        state.isPending = true;
        state.selectedSort = value;
        state.currentPage = 1; // Reset pagination when sort changes

        if ( value === t( "toolbar.sort.options.newsPage.newest" ) ) {
            state.shownPosts = state.shownPosts.sort(
                ( a, b ) => new Date( b.date ) - new Date( a.date ),
            );
        } else if ( value === t( "toolbar.sort.options.newsPage.oldest" ) ) {
            state.shownPosts = state.shownPosts.sort(
                ( a, b ) => new Date( a.date ) - new Date( b.date ),
            );
        }
        mockLoading();
    }

    watch(
        () => state.selectedDate,
        ( value ) => handleDateChange( value ),
        { deep: true },
    );
    function handleDateChange ( value ) {
        if ( !value.start || !value.end ) {
            state.shownPosts = state.posts;
            return;
        }

        state.isPending = true;
        state.currentPage = 1; // Reset pagination when date changes
        state.selectedDate = value;
        if (
            ( value.start && !value.end ) ||
            df.format( value.start?.toDate() ) === df.format( value.end?.toDate() )
        ) {
            state.shownPosts = state.posts.filter(
                ( post ) =>
                    df.format( new Date( post.date ) ) ===
                    df.format( value.start.toDate() ),
            );
        } else if ( value.start && value.end ) {
            if ( value.start.toDate() > value.end.toDate() ) {
                // swap the start and end date
                const temp = value.start;
                value.start = value.end;
                value.end = temp;
            }
            state.shownPosts = state.posts.filter(
                ( post ) =>
                    new Date( post.date ) >= value.start.toDate() &&
                    new Date( post.date ) <= value.end.toDate(),
            );
        } else {
            state.shownPosts = state.posts;
        }
        mockLoading();
    }
    function handleDateClear () {
        state.isPending = true;
        state.selectedDate = { start: null, end: null };
        state.shownPosts = state.posts;
        mockLoading();
    }

    // Create the context object with all state and methods
    const context = {
        state: state,
        filterOptions,
        sortOptions,

        // Methods
        fetchPosts,
        handlePageChange,
        handleSearchChange,
        handleCancelSearch,
        handleFilterChange,
        handleSortChange,
        handleDateChange,
        handleDateClear,
    };

    // Provide the context to child components
    provide( PostsSymbol, context );

    return context;
}

// Composable for child components to use the posts context
export function usePostsContext () {
    const context = inject( PostsSymbol );
    if ( !context ) {
        throw new Error(
            "usePostsContext must be used within a component where createPostsContext has been called",
        );
    }
    return context;
}
